import { useEffect, useState, useCallback, useMemo } from "react";
import { LoanApplicationService } from "../../services/LoanApplication";
import {
  applyDashboardInfoUiConfig,
  getRenderStrategy
} from "../../core/access-control";
import type {
  DashboardInfoResponse,
  DashboardInfoSidebar,
  DashboardInfoTab
} from "../../core/access-control";

/** DashboardInfoTab is the authoritative raw-tab shape (see dashboardInfoProvider.ts). */
type Tab = DashboardInfoTab;

const EMPTY_RESPONSE: DashboardInfoResponse = {};
const DEFAULT_RESOURCE_NAME = "dashboard_info";

export interface UseTabsFromUIConfigParams {
  token: string;
  uniqueID: string;
  isLenderDashboard: boolean;
  loanID?: string;
  userID?: string;
  pageName?: string;
  resourceName?: string;
}

export interface TabsFromUIConfig {
  tabs: Tab[];
  sidebar: DashboardInfoSidebar;
  isLoading: boolean;
}

/**
 * Fetches this page's dashboard_info UI config and derives its visible tabs.
 *
 * Also feeds the response to applyDashboardInfoUiConfig, so button visibility
 * on the page is answered through accessControl.can() like every other check.
 * Shared across entities via `resourceName` — leads and other tab-less pages
 * simply omit loanID/userID.
 */
export function useTabsFromUIConfig({
  token,
  uniqueID,
  isLenderDashboard,
  loanID,
  userID,
  pageName,
  resourceName = DEFAULT_RESOURCE_NAME
}: UseTabsFromUIConfigParams): TabsFromUIConfig {
  const willFetch =
    !!token && !!uniqueID && getRenderStrategy().isConfigDrivenTabs;

  const [uiTabs, setUITabs] = useState<Tab[]>([]);
  const [sidebar, setSidebar] = useState<DashboardInfoSidebar>({});
  const [isLoading, setIsLoading] = useState<boolean>(willFetch);

  const fetchTabsFromUIConfig =
    useCallback(async (): Promise<DashboardInfoResponse> => {
      if (!token || !uniqueID || !getRenderStrategy().isConfigDrivenTabs) {
        return EMPTY_RESPONSE;
      }

      try {
        const response: any = await LoanApplicationService.getTabsFromUIConfig({
          loanID,
          userID,
          token,
          uniqueID,
          isLenderDashboard,
          pageName,
          resourceName
        });

        return response?.status && response?.data
          ? response.data
          : EMPTY_RESPONSE;
      } catch (error) {
        console.error("Error fetching tabs config", error);
        return EMPTY_RESPONSE;
      }
    }, [
      loanID,
      userID,
      token,
      uniqueID,
      isLenderDashboard,
      pageName,
      resourceName
    ]);

  useEffect(() => {
    if (!willFetch) {
      setIsLoading(false);
      return;
    }

    let isCurrent = true;
    setIsLoading(true);

    fetchTabsFromUIConfig().then((data) => {
      if (!isCurrent) return;

      const dashboardInfo = data?.ruleData?.uiElements?.uiDashboardInfo;
      setUITabs(dashboardInfo?.tabs ?? []);
      setSidebar(dashboardInfo?.sidebar ?? {});
      // pageName doubles as this page's casbinResource for tab-less pages
      // (e.g. Utilities) whose buttons come back flat, not nested under a tab.
      // No-op for tabbed pages, which register via each tab's casbinResource.
      applyDashboardInfoUiConfig(data, pageName);
      setIsLoading(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [willFetch, pageName, fetchTabsFromUIConfig]);

  const derivedTabs = useMemo(
    () =>
      uiTabs
        .filter((tab) => tab?.visible?.isVisible)
        .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
        .map((tab) => ({
          ...tab,
          href: tab.route,
          current: false
        })),
    [uiTabs]
  );

  return useMemo(
    () => ({ tabs: derivedTabs, sidebar, isLoading }),
    [derivedTabs, sidebar, isLoading]
  );
}
